import React, { useEffect, useMemo, useState } from "react";
import { Tag, Modal, Button, Tooltip, Timeline, Drawer } from "antd";
import Table from "../../../core/common/dataTable/index";
import { Link, useLocation } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../router/all_routes";
import { Dropdown } from "react-bootstrap";
import { IoInformationCircle, IoCheckmarkCircle } from "react-icons/io5";
import type { IconType } from "react-icons";
import dayjs from "dayjs";
import "./calendar.css";
import Form from "react-bootstrap/Form";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import {
  postRegularization,
  getSelfRegularizations,
  getAllRegularizations,
  RegularizationRecord,
  RegularizationCreate,
} from "../../../core/api/regularization";
import { toast } from "react-toastify";
import {
  AttendanceRecord,
  getAllAttendance,
  getSelfAttendance,
} from "../../../core/api/useAttendance";
import { useSelector } from "react-redux";
import { FaCheckCircle } from "react-icons/fa";
import { CheckCircleFilled } from "@ant-design/icons";
import useGetEmployees from "../../../core/api/useGetEmployees";

const routes = all_routes;

const statusCodeMap: Record<number, string> = {
  0: "Present",
  1: "Late Coming",
  2: "Present",
  3: "Early Out",
  4: "Missed Check Out",
};

type Props = {};

const statusStyles: Record<
  string,
  { bg: string; color: string; label: string; icon?: IconType }
> = {
  Present: { label: "Present", bg: "#e8f5e8", color: "#2e7d32" },
  "Late Coming": {
    label: "Late Coming",
    bg: "#fff8e1",
    color: "#f57c00",
    icon: IoInformationCircle,
  },
  "Early Out": {
    label: "Early Out",
    bg: "#fff3e0",
    color: "#ff9800",
    icon: IoInformationCircle,
  },
  "Missed Check Out": {
    label: "Missed Check Out",
    bg: "#ffebee",
    color: "#d32f2f",
    icon: IoInformationCircle,
  },
  "Half Day": {
    label: "Half Day",
    bg: "#fff9c4",
    color: "#f57f17",
  },
  "Sick Leave": {
    label: "Sick Leave",
    bg: "#e8f5e8",
    color: "#388e3c",
  },
  Absent: {
    label: "Absent",
    bg: "#ffebee",
    color: "#d32f2f",
  },
  "Week Off": {
    label: "Week Off",
    bg: "#fff3e0",
    color: "#ff9800",
  },
};

const getStatusStyle = (status: string) =>
  statusStyles[status] || statusStyles["Present"];

const TimeSheetAdmin = () => {
  const [activeTab, setActiveTab] = useState("calendar");
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [regularizations, setRegularizations] = useState<
    RegularizationRecord[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRegModal, setShowRegModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("2025-07-16");
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(
    null
  );
  const [selectedRegularization, setSelectedRegularization] =
    useState<RegularizationRecord | null>(null);
  const [reason, setReason] = useState("");
  const { employees, isLoading: loadingEmployees } = useGetEmployees();
  const empToken = useAuthToken();
  const empRole = useSelector((state: any) => state.auth.role);
  const isAdmin = empRole === "Admin";
  const [type, setType] = useState("");
  const [notify, setNotify] = useState("");
  const location = useLocation();

  // Extract employee_id from location state instead of query params
  const employeeIdFromState = location.state?.employee_id;

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });
    return map;
  }, [employees]);

  useEffect(() => {
    const fetch = async () => {
      if (!empToken) return;
      setLoading(true);
      try {
        let attendanceData: AttendanceRecord[] = [];
        let regularizationData: RegularizationRecord[] = [];

        if (empRole !== "Employee" && employeeIdFromState) {
          // Fetch all data and filter by employee_id from state
          const [allAttendance, allRegularizations] = await Promise.all([
            getAllAttendance(empToken),
            getAllRegularizations(empToken),
          ]);
          attendanceData = Array.isArray(allAttendance)
            ? allAttendance.filter(
                (record) => record.employee_id === employeeIdFromState
              )
            : [];
          regularizationData = Array.isArray(allRegularizations)
            ? allRegularizations.filter(
                (record) => record.employee_id === employeeIdFromState
              )
            : [];
        } else {
          // For Employee or no employee_id, fetch self data
          const [selfAttendance, selfRegularizations] = await Promise.all([
            getSelfAttendance(empToken),
            getSelfRegularizations(empToken),
          ]);
          attendanceData = Array.isArray(selfAttendance) ? selfAttendance : [];
          regularizationData = Array.isArray(selfRegularizations)
            ? selfRegularizations
            : [];
        }

        setAttendance(attendanceData);
        setRegularizations(regularizationData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAttendance([]);
        setRegularizations([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [empToken, empRole, employeeIdFromState]);

  const hasRegularization = (date: string) => {
    return regularizations.some((reg) => reg.timesheet_date === date);
  };

  const getRegularizationForDate = (date: string) => {
    return regularizations.find((reg) => reg.timesheet_date === date);
  };

  const handleOpenModal = (date: string, record?: AttendanceRecord) => {
    setSelectedDate(date);
    setSelectedRecord(record || null);

    const existingReg = getRegularizationForDate(date);
    if (existingReg) {
      setSelectedRegularization(existingReg);
      setShowRegModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowRegModal(false);
    setReason("");
    setType("");
    setNotify("");
    setSelectedRecord(null);
    setSelectedRegularization(null);
  };

  const handleSubmitModal = async () => {
    if (!type || !reason) {
      toast.warning("Please select both Type and Reason");
      return;
    }

    if (!empToken || !selectedRecord) {
      console.error("Missing required data");
      return;
    }

    const payload: RegularizationCreate = {
      attendance_id: selectedRecord.id || selectedRecord.id || 1,
      reg_type: type,
      reason: reason,
      notify: notify,
    };
    try {
      await postRegularization(payload, empToken);
      handleCloseModal();

      let attendanceData: AttendanceRecord[] = [];
      let regularizationData: RegularizationRecord[] = [];

      if (empRole !== "Employee" && employeeIdFromState) {
        const [allAttendance, allRegularizations] = await Promise.all([
          getAllAttendance(empToken),
          getAllRegularizations(empToken),
        ]);
        attendanceData = Array.isArray(allAttendance)
          ? allAttendance.filter(
              (record) => record.employee_id === employeeIdFromState
            )
          : [];
        regularizationData = Array.isArray(allRegularizations)
          ? allRegularizations.filter(
              (record) => record.employee_id === employeeIdFromState
            )
          : [];
      } else {
        const [selfAttendance, selfRegularizations] = await Promise.all([
          getSelfAttendance(empToken),
          getSelfRegularizations(empToken),
        ]);
        attendanceData = Array.isArray(selfAttendance) ? selfAttendance : [];
        regularizationData = Array.isArray(selfRegularizations)
          ? selfRegularizations
          : [];
      }

      setAttendance(attendanceData);
      setRegularizations(regularizationData);
    } catch (error) {
      console.error("Post regularization failed:", error);
    }
  };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const isWeekOff = (date: dayjs.Dayjs) => {
    return date.day() === 0;
  };

  const createCalendarData = () => {
    const calendarData: Record<
      string,
      Record<
        string,
        {
          status: string;
          record: AttendanceRecord;
          hasRegularization: boolean;
          regularization?: RegularizationRecord;
        }
      >
    > = {};

    attendance.forEach((record) => {
      const date = record.attendance_date || record.date || record.created_date;
      if (!date) return;

      const dateObj = dayjs(date);
      const monthKey = dateObj.format("YYYY-MM");
      const dayKey = dateObj.format("DD");
      const fullDate = dateObj.format("YYYY-MM-DD");

      if (!calendarData[monthKey]) {
        calendarData[monthKey] = {};
      }

      const regularization = getRegularizationForDate(fullDate);
      const hasReg = hasRegularization(fullDate);
      const isRegApproved = regularization?.status === "Approved";

      let statusString: string;

      if (isWeekOff(dateObj)) {
        statusString = "Week Off";
      } else {
        if (record.today_status === null || record.today_status === undefined) {
          statusString = record.status || "Present";
        } else {
          statusString = statusCodeMap[record.today_status] || "Present";
        }
      }

      calendarData[monthKey][dayKey] = {
        status: statusString,
        record,
        hasRegularization: hasReg,
        regularization,
      };
    });

    return calendarData;
  };

  const calendarData = createCalendarData();

  const columns = [
    {
      title: "Date",
      key: "date",
      width: 120,
      render: (_: any, record: AttendanceRecord) => {
        const rawDate =
          record.attendance_date || record.date || record.created_date;
        if (!rawDate) return "-";

        const dateObj = dayjs(rawDate);
        const formattedDate = dateObj.format("DD-MM-YYYY");
        const dayShort = dateObj.format("ddd");

        const badgeColor = isWeekOff(dateObj)
          ? "#fd7e14"
          : dayShort === "Sat"
          ? "#fd0d91ff"
          : "#1763AD";

        return (
          <div className="text-center d-flex flex-column align-items-center gap-1">
            <div className="text-dark">
              <b>{formattedDate}</b>
            </div>
            <span
              className="badge"
              style={{
                backgroundColor: badgeColor,
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
              }}
            >
              {dayShort}
            </span>
          </div>
        );
      },
    },
    {
      title: "Check In & Out",
      key: "checkinout",
      width: 180,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);

        if (isWeekOff(dateObj)) {
          return (
            <div style={{ color: "#666", fontStyle: "italic" }}>Week Off</div>
          );
        }

        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;

        const formatTime = (time: string) => dayjs(time).format("h:mm A");

        const getBadge = (iconClass: string, timeValue: string | null) => (
          <div
            className={`badge ${
              iconClass.includes("logout")
                ? "bg-danger-subtle text-danger"
                : "bg-success-subtle text-success"
            }`}
            style={{
              fontSize: "10px",
              padding: "6px 10px",
              width: "100px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              borderRadius: "4px",
            }}
          >
            <i className={`ti ${iconClass}`} style={{ fontSize: "12px" }} />
            <span style={{ fontSize: "11px", fontWeight: 500 }}>
              {timeValue ? formatTime(timeValue) : "Pending"}
            </span>
          </div>
        );

        return (
          <div className="d-flex flex-column align-items-center gap-1">
            {getBadge(isPunch ? "ti-fingerprint" : "ti-device-laptop", checkIn)}
            {getBadge("ti-logout", checkOut)}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);

        if (isWeekOff(dateObj)) {
          return <Tag color="orange">Week Off</Tag>;
        }

        const label = record.status || "Present";
        const color = label === "Present" ? "green" : "red";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Production",
      key: "production_hr",
      width: 120,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);

        if (isWeekOff(dateObj)) {
          return "-";
        }

        const production = record.production_hr || record.working_hours || "--";

        return (
          <span className="d-inline-flex align-items-center">
            <span
              className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
              style={{
                width: "20px",
                height: "20px",
                minWidth: "20px",
                minHeight: "20px",
                maxWidth: "20px",
                maxHeight: "20px",
                flexShrink: 0,
                flexGrow: 0,
              }}
            >
              <i className="ti ti-clock-hour-9" style={{ fontSize: "12px" }} />
            </span>
            <span className="text-dark">{production}</span>
          </span>
        );
      },
    },
    {
      title: "Break Time",
      key: "breaktime",
      width: 120,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);

        if (isWeekOff(dateObj)) {
          return "-";
        }

        return (
          <span className="d-inline-flex align-items-center justify-content-center">
            <span
              className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
              style={{
                width: "20px",
                height: "20px",
                minWidth: "20px",
                fontSize: "12px",
                flexShrink: 0,
              }}
            >
              <i className="ti ti-clock-pause" />
            </span>
            {record.Break || "1hr"}
          </span>
        );
      },
    },
    {
      title: "Late/Early",
      key: "lateearly",
      width: 150,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);

        if (isWeekOff(dateObj)) {
          return "-";
        }

        return (
          <div className="d-flex flex-column align-items-center gap-1">
            <div className="d-inline-flex align-items-center text-danger gap-2">
              <span style={{ fontSize: "12px" }}>{record.late || "--"}</span>
            </div>
            <div className="d-inline-flex align-items-center text-warning gap-2">
              <span style={{ fontSize: "12px" }}>
                {record.earlyout || "--"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Regularization",
      key: "regularization",
      width: 150,
      align: "center" as const,
      render: (_: any, record: AttendanceRecord) => {
        const date =
          record.attendance_date || record.date || record.created_date;
        const dateObj = dayjs(date);
        const fullDate = dateObj.format("YYYY-MM-DD");

        if (isWeekOff(dateObj)) {
          return "-";
        }

        const regularization = getRegularizationForDate(fullDate);

        if (regularization) {
          const statusColor =
            regularization.status === "Approved"
              ? "green"
              : regularization.status === "Rejected"
              ? "red"
              : "orange";
          return (
            <Tag color={statusColor}>
              <i
                className={`ti ${
                  regularization.status === "Approved"
                    ? "ti-thumb-up-filled"
                    : regularization.status === "Rejected"
                    ? "ti-thumb-down-filled"
                    : "ti-clock"
                } me-1`}
              />
              {regularization.status}
            </Tag>
          );
        }

        return record.regularized ? (
          <Tag color="red">
            <i className="ti ti-thumb-down-filled me-1" />
            Need Improvement
          </Tag>
        ) : (
          <Tag color="green">
            <i className="ti ti-thumb-up-filled me-1" />
            Great Job
          </Tag>
        );
      },
    },
  ];

  const CustomTooltip = ({
    content,
    children,
  }: {
    content: React.ReactNode;
    children: React.ReactNode;
  }) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div
        style={{ position: "relative", display: "inline-block" }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        {children}
        {visible && content && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginTop: "6px",
              padding: "6px 10px",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              borderRadius: "4px",
              fontSize: "12px",
              whiteSpace: "nowrap",
              zIndex: 1000,
            }}
          >
            {content}
          </div>
        )}
      </div>
    );
  };

  const renderCalendar = () => {
    const startDate = dayjs(`${selectedYear}-${selectedMonth + 1}-01`);
    const totalDays = startDate.daysInMonth();
    const firstDay = startDate.day();
    const totalCells = Math.ceil((firstDay + totalDays) / 7) * 7;

    const dates = Array.from({ length: totalCells }, (_, i) => {
      const day = i - firstDay + 1;
      if (day > 0 && day <= totalDays) {
        const currentDate = dayjs(
          `${selectedYear}-${selectedMonth + 1}-${day}`
        );
        const dateKey = day.toString().padStart(2, "0");
        const monthKey = `${selectedYear}-${(selectedMonth + 1)
          .toString()
          .padStart(2, "0")}`;

        const isSunday = isWeekOff(currentDate);
        const attendanceData = calendarData[monthKey]?.[dateKey];
        const status = isSunday ? "Week Off" : attendanceData?.status || null;
        const record = attendanceData?.record;
        const hasReg = attendanceData?.hasRegularization || false;
        const regularization = attendanceData?.regularization;

        return {
          day,
          status,
          record,
          date: currentDate,
          hasRegularization: hasReg,
          regularization,
        };
      }
      return null;
    });

    return (
      <div className="calendar-grid">
        <div className="calendar-row calendar-header-row text-center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="calendar-header-cell">
              {d}
            </div>
          ))}
        </div>
        {Array.from({ length: totalCells / 7 }).map((_, weekIndex) => (
          <div className="calendar-row" key={weekIndex}>
            {dates.slice(weekIndex * 7, weekIndex * 7 + 7).map((cell, idx) => (
              <div key={idx} className="calendar-cell">
                {cell && (
                  <>
                    <div
                      className="day-number"
                      style={{
                        color: cell.date.day() === 0 ? "#ff9800" : "#212529",
                      }}
                    >
                      {cell.day}
                    </div>
                    {cell.status &&
                      (() => {
                        const style = getStatusStyle(cell.status);
                        const Icon = style.icon;

                        const isClickable = [
                          "Late Coming",
                          "Early Out",
                          "Missed Check Out",
                          "Absent",
                        ].includes(cell.status);

                        const onClickHandler = () => {
                          if (isClickable && cell.record) {
                            const fullDate = cell.date.format("YYYY-MM-DD");
                            handleOpenModal(fullDate, cell.record);
                          }
                        };

                        const badgeContent = (
                          <div
                            className="badge"
                            style={{
                              background: style.bg,
                              color: style.color,
                              cursor: isClickable ? "pointer" : "default",
                              fontSize: "11px",
                              fontWeight: "500",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              position: "relative",
                            }}
                            onClick={onClickHandler}
                          >
                            {style.label}
                            {Icon &&
                              React.createElement(Icon as React.ElementType, {
                                style: { fontSize: 14 },
                              })}

                            {cell.hasRegularization &&
                              cell.regularization?.status === "Approved" &&
                              isClickable && (
                                <CheckCircleFilled
                                  style={{
                                    position: "absolute",
                                    top: "-4px",
                                    right: "-4px",
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    color: "#28a745",
                                    fontSize: "14px",
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                          </div>
                        );

                        return cell.record && cell.status !== "Week Off" ? (
                          <div className="custom-tooltip-wrapper">
                            {badgeContent}
                            <div className="custom-tooltip">
                              <div
                                className={`badge ${
                                  cell.record.punch_in || cell.record.check_in
                                    ? "bg-success-subtle text-success"
                                    : "bg-secondary-subtle text-muted"
                                }`}
                                style={{
                                  fontSize: "10px",
                                  padding: "6px 10px",
                                  width: "100px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: "6px",
                                  borderRadius: "4px",
                                }}
                              >
                                <i
                                  className={`ti ${
                                    cell.record.punch_in
                                      ? "ti-fingerprint"
                                      : "ti-device-laptop"
                                  }`}
                                  style={{ fontSize: "12px" }}
                                />
                                <span
                                  style={{ fontSize: "11px", fontWeight: 500 }}
                                >
                                  {cell.record.punch_in || cell.record.check_in
                                    ? dayjs(
                                        cell.record.punch_in ||
                                          cell.record.check_in
                                      ).format("h:mm A")
                                    : ""}
                                </span>
                              </div>
                              <div
                                className={`badge ${
                                  cell.record.punch_out || cell.record.check_out
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-secondary-subtle text-muted"
                                }`}
                                style={{
                                  fontSize: "10px",
                                  padding: "6px 10px",
                                  width: "100px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: "6px",
                                  borderRadius: "4px",
                                }}
                              >
                                <i
                                  className="ti ti-logout"
                                  style={{ fontSize: "12px" }}
                                />
                                <span
                                  style={{ fontSize: "11px", fontWeight: 500 }}
                                >
                                  {cell.record.punch_out ||
                                  cell.record.check_out
                                    ? dayjs(
                                        cell.record.punch_out ||
                                          cell.record.check_out
                                      ).format("h:mm A")
                                    : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          badgeContent
                        );
                      })()}
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const getFilteredAttendanceData = () => {
    return attendance.filter((record) => {
      const date = record.attendance_date || record.date || record.created_date;
      if (!date) return false;

      const recordDate = dayjs(date);
      return (
        recordDate.month() === selectedMonth &&
        recordDate.year() === selectedYear
      );
    });
  };

  const filteredAttendance = getFilteredAttendanceData();

  const getModalFields = (status: string) => {
    switch (status) {
      case "Late Coming":
        return ["date", "checkIn", "type", "reason"];
      case "Missed Check Out":
        return ["date", "checkIn", "checkOut", "type", "reason"];
      case "Early Out":
        return ["date", "checkIn", "checkOut", "type", "reason"];
      case "Absent":
        return ["date", "type", "reason"];
      default:
        return ["date", "checkIn", "checkOut", "type", "reason"];
    }
  };
  const modalFields = selectedRecord
    ? getModalFields(
        selectedRecord.today_status == null
          ? selectedRecord.status || "Present"
          : statusCodeMap[selectedRecord.today_status] || "Present"
      )
    : [];

  const formatTimeForDisplay = (timeString: string | null) => {
    if (!timeString) return "";

    try {
      const time = dayjs(timeString);
      if (time.isValid()) {
        return time.format("h:mm A");
      }

      const timeOnly = dayjs(`2000-01-01 ${timeString}`);
      if (timeOnly.isValid()) {
        return timeOnly.format("h:mm A");
      }

      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Timesheet -  {employeeIdFromState && employeeNameMap[employeeIdFromState]
                ? `${employeeNameMap[employeeIdFromState].name} (${employeeNameMap[employeeIdFromState].department})`
                : "Timesheet"}
            </h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Timesheets
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            {empRole !== "Employee" && (
              <div className="me-2 mb-2">
                <Link to={routes.attendanceDash}>
                  <button
                    className="btn btn-primary d-flex justify-content-center align-items-center"
                    style={{ boxShadow: "none", outline: "none" }}
                  >
                    <i className="ti ti-arrow-left me-1" />
                    Back
                  </button>
                </Link>
              </div>
            )}
            <div className="me-2 mb-2">
              <Dropdown.Toggle
                as="button"
                id="dropdown-export"
                className="btn btn-primary d-flex justify-content-center align-items-center"
                style={{ boxShadow: "none", outline: "none" }}
              >
                <i className="ti ti-file-export me-1" />
                Export
              </Dropdown.Toggle>
            </div>
            <div className="ms-0">
              <CollapseHeader />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <ul className="nav nav-tabs nav-tabs-top mb-0">
                <li className="nav-item">
                  <Link
                    to="#"
                    className={`nav-link ${
                      activeTab === "calendar" ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("calendar");
                    }}
                  >
                    Classic
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="#"
                    className={`nav-link ${
                      activeTab === "detailed" ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("detailed");
                    }}
                  >
                    Detailed
                  </Link>
                </li>
              </ul>
              <Dropdown className="ms-auto">
                <Dropdown.Toggle variant="light" className="border">
                  {monthNames[selectedMonth]} - {selectedYear}
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-2 dropdown-menu-end">
                  <div className="d-flex flex-wrap">
                    {monthNames.map((month, idx) => (
                      <button
                        key={month}
                        onClick={() => setSelectedMonth(idx)}
                        className="btn btn-outline-primary btn-sm m-1"
                      >
                        {month}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2">
                    <input
                      type="number"
                      className="form-control"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(+e.target.value)}
                    />
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="tab-content">
              {activeTab === "calendar" && (
                <div className="tab-pane fade show active">
                  {loading ? (
                    <div className="text-center p-4">Loading calendar...</div>
                  ) : (
                    renderCalendar()
                  )}
                </div>
              )}
              {activeTab === "detailed" && (
                <div
                  className="tab-pane fade show active"
                  style={{ overflowX: "auto" }}
                >
                  <Table
                    columns={columns}
                    dataSource={filteredAttendance}
                    bordered
                    rowKey={(record) =>
                      record.id || `${record.employee_id}-${record.date}`
                    }
                    loading={loading}
                    size="small"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title="📌 Attendance Regularization"
        placement="right"
        onClose={handleCloseModal}
        open={showModal}
        width={400}
        destroyOnClose={true}
      >
        <div style={{ padding: "1rem 0" }}>
          {modalFields.includes("date") && (
            <div className="mb-3">
              <label className="form-label fw-bold">
                Date <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={dayjs(selectedDate).format("DD-MM-YYYY")}
                disabled
              />
            </div>
          )}
          {modalFields.includes("checkIn") && (
            <div className="mb-3">
              <label className="form-label fw-bold">
                {selectedRecord?.punch_in || selectedRecord?.punch_out
                  ? "Punch In Time"
                  : "Check-In Time"}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formatTimeForDisplay(
                  (selectedRecord?.check_in || selectedRecord?.punch_in) ?? null
                )}
                disabled
              />
            </div>
          )}
          {modalFields.includes("checkOut") && (
            <div className="mb-3">
              <label className="form-label fw-bold">
                {selectedRecord?.punch_in || selectedRecord?.punch_out
                  ? "Punch Out Time"
                  : "Check-Out Time"}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formatTimeForDisplay(
                  (selectedRecord?.check_out || selectedRecord?.punch_out) ??
                    "Missed"
                )}
                disabled
              />
            </div>
          )}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Type <span className="text-danger">*</span>
            </label>
            <Form.Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
            >
              <option value="">Select Type</option>
              <option value="Late">Late</option>
              <option value="Absent">Absent</option>
              <option value="Early">Early</option>
              <option value="Missed">Missed out</option>
              <option value="OD">OD</option>
              <option value="WFH">WFH</option>
              <option value="Leave">Leave</option>
            </Form.Select>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">
              Reason <span className="text-danger">*</span>
            </label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select Reason</option>
              <option value="Traffic">Traffic</option>
              <option value="Health Issue">Health Issue</option>
              <option value="Permission">Permission</option>
              <option value="Technical">Technical Issue</option>
              <option value="Other">Other</option>
            </Form.Select>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">
              Notify <span className="text-danger">*</span>
            </label>
            <Form.Select
              value={notify}
              onChange={(e) => setNotify(e.target.value)}
              required
            >
              <option value="">Select Notify</option>
              {employees
                .filter(
                  (emp) =>
                    emp.role_name === "Admin" || emp.role_name === "Manager"
                )
                .map((emp) => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                  </option>
                ))}
            </Form.Select>
          </div>
          <div className="d-flex justify-content-end mt-4">
            <Button onClick={handleCloseModal} className="me-2">
              Cancel
            </Button>
            <Button type="primary" onClick={handleSubmitModal}>
              Apply Request
            </Button>
          </div>
        </div>
      </Drawer>
      {/* Regularization Status Modal */}
      {showRegModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              width: 700,
              backgroundColor: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background: "#1e3a8a",
                color: "white",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 56,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Attendance Regularization
              </h3>
              <button
                onClick={handleCloseModal}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 18,
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.2)";
                  (e.target as HTMLElement).style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.1)";
                  (e.target as HTMLElement).style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>
            {/* Content */}
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {selectedRegularization && (
                <>
                  {/* Top Badge Section */}
                  <div
                    style={{
                      backgroundColor: "#e3f2fd",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "24px",
                      border: "1px solid #bbdefb",
                    }}
                  >
                    {/* Headers Row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "20px",
                        marginBottom: "16px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <h6
                          style={{
                            margin: 0,
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          Check-in & Out
                        </h6>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <h6
                          style={{
                            margin: 0,
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          Date
                        </h6>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <h6
                          style={{
                            margin: 0,
                            color: "#666",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          Type
                        </h6>
                      </div>
                    </div>
                    {/* Content Row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "20px",
                        alignItems: "center",
                      }}
                    >
                      {/* Check-in & Out Badges */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        {/* Check In Badge */}
                        <div
                          style={{
                            backgroundColor:
                              selectedRegularization.check_in_time
                                ? "#d4edda"
                                : "#f8f9fa",
                            color: selectedRegularization.check_in_time
                              ? "#155724"
                              : "#6c757d",
                            fontSize: "10px",
                            padding: "8px 12px",
                            width: "120px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "6px",
                            borderRadius: "6px",
                            border: selectedRegularization.check_in_time
                              ? "1px solid #c3e6cb"
                              : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className="ti ti-fingerprint"
                            style={{ fontSize: "12px" }}
                          />
                          <span style={{ fontSize: "11px", fontWeight: 500 }}>
                            {selectedRegularization.check_in_time
                              ? formatTimeForDisplay(
                                  selectedRegularization.check_in_time
                                )
                              : "N/A"}
                          </span>
                        </div>
                        {/* Check Out Badge */}
                        <div
                          style={{
                            backgroundColor:
                              selectedRegularization.check_out_time
                                ? "#f8d7da"
                                : "#f8f9fa",
                            color: selectedRegularization.check_out_time
                              ? "#721c24"
                              : "#6c757d",
                            fontSize: "10px",
                            padding: "8px 12px",
                            width: "120px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "6px",
                            borderRadius: "6px",
                            border: selectedRegularization.check_out_time
                              ? "1px solid #f5c6cb"
                              : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className="ti ti-logout"
                            style={{ fontSize: "12px" }}
                          />
                          <span style={{ fontSize: "11px", fontWeight: 500 }}>
                            {selectedRegularization.check_out_time
                              ? formatTimeForDisplay(
                                  selectedRegularization.check_out_time
                                )
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                      {/* Date */}
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            backgroundColor: "#fff3cd",
                            color: "#856404",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 500,
                            border: "1px solid #ffeeba",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <i
                            className="ti ti-calendar"
                            style={{ fontSize: "12px" }}
                          />
                          {dayjs(selectedRegularization.timesheet_date).format(
                            "DD-MM-YYYY"
                          )}
                        </div>
                      </div>
                      {/* Type */}
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 500,
                            border: "1px solid #bbdefb",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {selectedRegularization.reg_type}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Timeline Section */}
                  <div style={{ marginBottom: "24px" }}>
                    <div
                      style={{
                        backgroundColor: "#fef7e0",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#744210",
                        }}
                      >
                        Attendance Regularization (
                        {selectedRegularization.reg_type})
                      </span>
                    </div>
                    {/* Timeline Line */}
                    <div style={{ position: "relative", paddingLeft: "30px" }}>
                      {/* Timeline Line */}
                      <div
                        style={{
                          position: "absolute",
                          left: "15px",
                          top: "0",
                          bottom: "0",
                          width: "2px",
                          backgroundColor: "#e9ecef",
                        }}
                      />
                      {/* Timeline Items */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "24px",
                        }}
                      >
                        {/* Applied Item */}
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "-22px",
                              top: "8px",
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              backgroundColor: "#2563eb",
                              border: "2px solid white",
                              boxShadow: "0 0 0 2px #e9ecef",
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: "#dbeafe",
                              borderRadius: "8px",
                              padding: "16px",
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#1763AD",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                display: "inline-block",
                                marginBottom: "8px",
                              }}
                            >
                              {selectedRegularization.reg_type} Regularization
                              Applied
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              {dayjs(selectedRegularization.created_at).format(
                                "DD-MMM-YYYY h:mm A"
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#374151",
                                marginBottom: "8px",
                              }}
                            >
                              {selectedRegularization.reason}
                            </div>
                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              <strong>Applied By:</strong>{" "}
                              {employeeNameMap[
                                selectedRegularization.employee_id
                              ]?.name || selectedRegularization.employee_id}
                            </div>
                          </div>
                        </div>
                        {/* Approved/Rejected Item (if not pending) */}
                        {selectedRegularization.status !== "Pending" && (
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                left: "-22px",
                                top: "8px",
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                                backgroundColor:
                                  selectedRegularization.status === "Approved"
                                    ? "#16a34a"
                                    : "#dc2626",
                                border: "2px solid white",
                                boxShadow: "0 0 0 2px #e9ecef",
                              }}
                            />
                            <div
                              style={{
                                backgroundColor:
                                  selectedRegularization.status === "Approved"
                                    ? "#dcfce7"
                                    : "#fee2e2",
                                borderRadius: "8px",
                                padding: "16px",
                                border:
                                  selectedRegularization.status === "Approved"
                                    ? "1px solid #bbf7d0"
                                    : "1px solid #fecaca",
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor:
                                    selectedRegularization.status === "Approved"
                                      ? "#16a34a"
                                      : "#dc2626",
                                  color: "white",
                                  padding: "4px 12px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  display: "inline-block",
                                  marginBottom: "8px",
                                }}
                              >
                                {selectedRegularization.reg_type} Regularization{" "}
                                {selectedRegularization.status}
                              </div>
                              {selectedRegularization.approved_date && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {dayjs(
                                    selectedRegularization.updated_at
                                  ).format("DD-MMM-YYYY h:mm A")}
                                </div>
                              )}
                              {selectedRegularization.approved_by && (
                                <div
                                  style={{ fontSize: "12px", color: "#6b7280" }}
                                >
                                  <strong>
                                    {selectedRegularization.status ===
                                    "Approved"
                                      ? "Approved"
                                      : "Rejected"}{" "}
                                    By:
                                  </strong>{" "}
                                  {employeeNameMap[
                                    selectedRegularization.approved_by
                                  ]?.name || selectedRegularization.approved_by}
                                </div>
                              )}
                              {selectedRegularization.reject_reason && (
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#374151",
                                    marginTop: "8px",
                                  }}
                                >
                                  <strong>Reject Reason:</strong>{" "}
                                  {selectedRegularization.reject_reason}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2024 - 2025 © IATT.</p>
        <p>
          Designed &amp; Developed By{" "}
          <Link to="#" className="text-primary">
            IATT
          </Link>
        </p>
      </div>
    </div>
  );
};
export default TimeSheetAdmin;
